import React, { useState, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import { FiSidebar, FiLogOut, FiUser, FiArrowLeft } from 'react-icons/fi';
import GlobalSearchDropdown from './GlobalSearchDropdown';
import { AuthContext } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { useCompanyLogo } from '../hooks/useCompanySettings';
import HeaderCustomerSelector from './HeaderCustomerSelector';
import routeRegistry from '../services/routeRegistry';
import useHeaderConfig from '../hooks/useHeaderConfig';

const MobileHeader = ({ showSidebarToggle = true, setSearchQuery, headerRef }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, userRoles, isSuperuser } = useContext(AuthContext);
    const {
        toggleLeftSidebar,
        toggleRightSidebar,
        rightVisible,
        leftCollapsed
    } = useSidebar();
    const { logo: companyLogo, loading: logoLoading } = useCompanyLogo();
    const { config, hasHeader } = useHeaderConfig();
    const [searchTerm, setSearchTerm] = useState("");

    const userRoleNames = Array.isArray(userRoles) ? userRoles : [];
    const effectiveRoles = isSuperuser ? [...userRoleNames, 'admin'] : userRoleNames;
    const hasProductCatalogAccess = user && routeRegistry.hasAccess('/', effectiveRoles);
    const hasCheckoutAccess = user && routeRegistry.hasAccess('/checkout', effectiveRoles);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchTerm.trim() && setSearchQuery) {
            setSearchQuery(searchTerm);
            if (location.pathname !== '/products' && location.pathname !== '/') {
                navigate(`/products?search=${encodeURIComponent(searchTerm.trim())}`);
            }
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (setSearchQuery) {
            setSearchQuery(value);
        }
    };

    const handleLeftToggle = () => {
        toggleLeftSidebar();
    };

    const handleRightToggle = () => {
        toggleRightSidebar();
    };

    const handleLogout = () => {
        if (window.confirm('Are you sure you want to logout?')) {
            logout();
            navigate('/login');
        }
    };

    if (!hasHeader) return null;

    return (
        <div ref={headerRef}>
            <header className="bg-white shadow-sm fixed top-0 left-0 right-0 z-50" style={{ minHeight: '56px' }}>
                <div className="px-2">
                    <div className="flex items-center justify-between h-14 gap-2">
                        <div className="flex items-center">
                            {config.show_left_sidebar_toggle && user && showSidebarToggle && (
                                <button
                                    className={`p-2 rounded-md transition-colors ${
                                        !leftCollapsed
                                            ? 'text-primary bg-primary/10'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                    }`}
                                    onClick={handleLeftToggle}
                                    title={!leftCollapsed ? "Close Navigation" : "Open Navigation"}
                                >
                                    <FiSidebar className="h-5 w-5" />
                                </button>
                            )}

                            {location.key !== 'default' && (
                                <button
                                    className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                                    onClick={() => navigate(-1)}
                                    title="Go Back"
                                >
                                    <FiArrowLeft className="h-5 w-5" />
                                </button>
                            )}

                            {config.show_logo && (
                                <Link to="/" className="flex items-center ml-2">
                                    {logoLoading ? (
                                        <div className="h-8 w-8" />
                                    ) : companyLogo && companyLogo.trim() !== '' ? (
                                        <img
                                            src={companyLogo}
                                            alt="Logo"
                                            className="h-8 w-auto object-contain"
                                        />
                                    ) : (
                                        <div className="h-8" />
                                    )}
                                </Link>
                            )}
                        </div>

                        {config.show_search ? (
                            config.search_type === 'global' ? (
                                <GlobalSearchDropdown
                                    placeholder={config.search_placeholder || "Search..."}
                                    onProductSearch={hasProductCatalogAccess ? setSearchQuery : null}
                                    className="flex-1 max-w-sm"
                                />
                            ) : hasProductCatalogAccess ? (
                                <div className="flex-1 max-w-sm">
                                    <form onSubmit={handleSearchSubmit} className="relative">
                                        <input
                                            type="text"
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                            placeholder={config.search_placeholder || "Search..."}
                                            className="w-full px-3 py-1.5 pr-8 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                        />
                                        <button
                                            type="submit"
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                                        >
                                            <FaSearch className="h-4 w-4" />
                                        </button>
                                    </form>
                                </div>
                            ) : (
                                <div className="flex-1"></div>
                            )
                        ) : (
                            <div className="flex-1"></div>
                        )}

                        <div className="flex items-center gap-1">
                            {config.show_user_info && user ? (
                                <>
                                    <button
                                        className="p-2 rounded-md text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors"
                                        onClick={handleLogout}
                                        title="Logout"
                                    >
                                        <FiLogOut className="h-5 w-5" />
                                    </button>

                                    {config.show_right_sidebar_toggle && (
                                        <button
                                            className={`p-2 rounded-md transition-colors ${
                                                rightVisible
                                                    ? 'text-primary bg-primary/10'
                                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                            }`}
                                            onClick={handleRightToggle}
                                            title={rightVisible ? "Close Quick Dashboard" : "Open Quick Dashboard"}
                                        >
                                            <FiSidebar className="h-5 w-5 scale-x-[-1]" />
                                        </button>
                                    )}
                                </>
                            ) : !user ? (
                                <Link
                                    to="/login"
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-blue-700 bg-white border border-blue-500 hover:bg-blue-50 transition-colors"
                                    title="Login"
                                >
                                    <FiUser className="h-4 w-4" />
                                    <span>Login</span>
                                </Link>
                            ) : null}
                        </div>
                    </div>
                </div>
            </header>

            <div style={{ height: '56px' }} />

            {config.show_customer_selector && hasCheckoutAccess && (
                <div className="sticky top-14 z-29 bg-white border-b border-gray-200 shadow-sm">
                    <div className="px-2 py-2">
                        <HeaderCustomerSelector />
                    </div>
                </div>
            )}
        </div>
    );
};

export default MobileHeader;
